// Header.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Link,
  Heading,
  Image,
  Button,
  IconButton,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  VStack,
  useBreakpointValue,
} from "@chakra-ui/react";
import { HamburgerIcon, CloseIcon } from "@chakra-ui/icons";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "./Images/himmats-logo.svg";
import arrow from "./Images/Button-Arrow.svg";

const Header = () => {
  const [activeNav, setActiveNav] = useState("Home");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { label: "Home", href: "#home" },
    { label: "Program", href: "#program" },
    { label: "Courses", href: "#courses" },
    { label: "Testimonials", href: "#testimonials" },
    { label: "Registration", href: "#registration" },
  ];

  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";
  }, []);

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
      setActiveNav(
        navItems.find((item) => item.href === location.hash)?.label || activeNav
      );
      if (isMobile) onClose();
    }
  }, [location.hash]);

  const navLinkStyles = {
    padding: "8px 24px",
    borderRadius: "24px",
    fontSize: "14px",
    fontWeight: "400",
    transition: "background 0.2s ease",
    _hover: { background: "#0069CA", color: "white" },
  };

  const handleNavClick = (href, label) => {
    setActiveNav(label);
    navigate(`/${href}`);  // e.g. "/#program"
  };

  return (
    <>
      <Flex
        as="header"
        position="sticky"
        top="0"
        zIndex="1000"
        bg="white"
        align="center"
        justify="space-between"
        w="100%"
        py="24px"
        px={{ base: "16px", md: "42px", lg: "94px" }}
        boxShadow="sm"
      >
        <Flex align="center" gap="8px">
          <Image src={logo} alt="logo-image" boxSize="35px" />
          <Heading color="#232424" fontSize={{ base: "18px", md: "21px" }}>
            Himmats
          </Heading>
        </Flex>

        {!isMobile && (
          <Flex flex="1" justify="center">
            <Box
              as="nav"
              borderRadius="24px"
              bg="#F6F6F6"
              backdropFilter="blur(10px) saturate(180%)"
              width="60%"
              maxWidth="700px"
              px="24px"
              py="6px"
            >
              <Flex as="ul" listStyleType="none" gap="24px" m={0} p={0} justify="center">
                {navItems.map(({ label, href }) => (
                  <Box as="li" key={label}>
                    <Link
                      {...navLinkStyles}
                      background={activeNav === label ? "#0069CA" : "transparent"}
                      color={activeNav === label ? "white" : "#000"}
                      onClick={() => handleNavClick(href, label)}
                    >
                      {label}
                    </Link>
                  </Box>
                ))}
              </Flex>
            </Box>
          </Flex>
        )}

        {!isMobile && (
          <Button
            px="24px"
            py="10px"
            borderRadius="24px"
            as="a"
            href="https://stande.co/login"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Flex align="center" gap="8px">
              <Heading fontSize="14px" fontWeight="400" color="#232424">
                Login
              </Heading>
              <Image src={arrow} alt="button-arrow-icon" boxSize="25px" />
            </Flex>
          </Button>
        )}

        {isMobile && (
          <IconButton
            icon={<HamburgerIcon boxSize="20px" color="#0069CA" />}
            onClick={onOpen}
            aria-label="Open Menu"
            bg="#EBF2F7"
            _hover={{ bg: "#D8E6F1" }}
            borderRadius="full"
            minW="44px"
            h="44px"
            p="0"
          />
        )}
      </Flex>

      <Drawer isOpen={isOpen} placement="top" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent bg="white" py="32px">
          <DrawerBody>
            <Flex justify="flex-end">
              <IconButton icon={<CloseIcon />} onClick={onClose} aria-label="Close Menu" />
            </Flex>
            <VStack spacing="24px" mt="24px">
              {navItems.map(({ label, href }) => (
                <Link
                  key={label}
                  fontSize="16px"
                  fontWeight="500"
                  color={activeNav === label ? "#0069CA" : "#232424"}
                  _hover={{ color: "#0069CA" }}
                  onClick={() => handleNavClick(href, label)}
                >
                  {label}
                </Link>
              ))}

              <Button
                mt="24px"
                px="24px"
                py="10px"
                borderRadius="24px"
                w="100%"
                maxW="300px"
                as="a"
                href="https://stande.co/login"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Flex align="center" gap="8px" justify="center">
                  <Heading fontSize="14px" fontWeight="400" color="#232424">
                    Login
                  </Heading>
                  <Image src={arrow} alt="button-arrow-icon" boxSize="25px" />
                </Flex>
              </Button>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default Header;
